"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Line, Doughnut, Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js"
import { useAuth } from "../../../contexts/AuthContext"
import {
  ChartBarIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  WalletIcon,
  ChartPieIcon,
  CheckCircleIcon,
  EyeIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  ClockIcon,
  RocketLaunchIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  UsersIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import Swal from "sweetalert2"

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
)

// Format INR currency
const formatCurrency = (value) => {
  const num = typeof value === "number" ? value : Number(value) || 0
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

function Reseller() {
  const navigate = useNavigate()
  const { user, getAllUsers, fetchReportStats, transferBalance, getUserBalance, apiCall } = useAuth()

  const [loading, setLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Data states
  const [resellerBalance, setResellerBalance] = useState(user?.balance || 0)
  const [subUsers, setSubUsers] = useState([])
  const [userStatsMap, setUserStatsMap] = useState({}) // { userId: { total, sent, delivered, read, failed } }
  const [safeToday, setSafeToday] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    pending: 0,
  })
  const [safeTotal, setSafeTotal] = useState({ total: 0 })
  const [yesterdayData, setYesterdayData] = useState({ total: 0, delivered: 0, read: 0 })
  const [weeklyData, setWeeklyData] = useState({
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    messages: [0, 0, 0, 0, 0, 0, 0],
    delivered: [0, 0, 0, 0, 0, 0, 0],
    read: [0, 0, 0, 0, 0, 0, 0],
    failed: [0, 0, 0, 0, 0, 0, 0],
  })

  // Table Search & Filter
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  // Transfer Balance Modal
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [selectedUserForTransfer, setSelectedUserForTransfer] = useState("")
  const [transferAmount, setTransferAmount] = useState("")
  const [transferDesc, setTransferDesc] = useState("")
  const [transferProcessing, setTransferProcessing] = useState(false)

  useEffect(() => {
    loadDashboardData()
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    const refreshTimer = setInterval(loadDashboardData, 60000)
    return () => {
      clearInterval(timer)
      clearInterval(refreshTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadDashboardData = async () => {
    const todayStr = new Date().toISOString().split("T")[0]
    const yesterdayDate = new Date(Date.now() - 24 * 60 * 60 * 1000)
    const yesterdayStr = yesterdayDate.toISOString().split("T")[0]

    try {
      // Fast initial load in 1 parallel batch
      const [balRes, usersRes, todayStatsRes, yesterdayStatsRes] = await Promise.all([
        getUserBalance ? getUserBalance().catch(() => null) : Promise.resolve(null),
        getAllUsers ? getAllUsers().catch(() => null) : Promise.resolve(null),
        fetchReportStats({ startDate: todayStr, endDate: todayStr }).catch(() => null),
        fetchReportStats({ startDate: yesterdayStr, endDate: yesterdayStr }).catch(() => null),
      ])

      if (balRes?.success && typeof balRes.balance === "number") {
        setResellerBalance(balRes.balance)
      }

      let managedUsers = []
      if (usersRes?.success && Array.isArray(usersRes.users)) {
        const currentUserId = String(user?._id || user?.id || "")
        managedUsers = usersRes.users.filter((u) => {
          if (String(u._id) === currentUserId) return false
          const creatorId = String(u.createdBy?._id || u.createdBy || "")
          const parentIds = (u.parentUsers || []).map((p) => String(p?._id || p))
          return creatorId === currentUserId || parentIds.includes(currentUserId)
        })
        setSubUsers(managedUsers)
      }

      if (todayStatsRes) {
        setSafeToday(todayStatsRes)
      }
      if (yesterdayStatsRes) {
        setYesterdayData(yesterdayStatsRes)
      }

      // Unblock UI immediately! Page renders in <200ms
      setLoading(false)

      // Async background loading for weekly trend and user stats
      loadWeeklyDataAsync()
      loadUserStatsAsync(managedUsers, todayStr)

    } catch (error) {
      console.error("Error loading Reseller Dashboard:", error)
      setLoading(false)
    }
  }

  const loadWeeklyDataAsync = async () => {
    try {
      const today = new Date()
      const dates = []
      const promises = []

      for (let i = 6; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        const dStr = d.toISOString().split("T")[0]
        dates.push({ label: d.toLocaleDateString("en-US", { weekday: "short" }), dStr })
        promises.push(fetchReportStats({ startDate: dStr, endDate: dStr }).catch(() => null))
      }

      const results = await Promise.all(promises)

      const weeklySent = []
      const weeklyDelivered = []
      const weeklyRead = []
      const weeklyFailed = []

      results.forEach((res) => {
        if (res) {
          weeklySent.push(res.sent || res.totalSent || 0)
          weeklyDelivered.push(res.delivered || 0)
          weeklyRead.push(res.read || 0)
          weeklyFailed.push(res.failed || 0)
        } else {
          weeklySent.push(0)
          weeklyDelivered.push(0)
          weeklyRead.push(0)
          weeklyFailed.push(0)
        }
      })

      setWeeklyData({
        labels: dates.map((d) => d.label),
        messages: weeklySent,
        delivered: weeklyDelivered,
        read: weeklyRead,
        failed: weeklyFailed,
      })
    } catch (err) {
      console.error("Error loading weekly trend async:", err)
    }
  }

  const loadUserStatsAsync = async (managedUsers, todayStr) => {
    if (!managedUsers || managedUsers.length === 0) return
    const statsMap = {}
    const batchSize = 10

    for (let i = 0; i < managedUsers.length; i += batchSize) {
      const batch = managedUsers.slice(i, i + batchSize)
      await Promise.all(
        batch.map(async (u) => {
          try {
            const res = await fetchReportStats({
              userId: u._id,
              startDate: todayStr,
              endDate: todayStr,
            })
            if (res) {
              statsMap[u._id] = {
                total: res.total || 0,
                sent: res.sent || res.totalSent || 0,
                delivered: res.delivered || 0,
                read: res.read || 0,
                failed: res.failed || 0,
              }
            }
          } catch (err) {
            // ignore
          }
        })
      )
      setUserStatsMap((prev) => ({ ...prev, ...statsMap }))
    }
  }

  // Filter users table
  const filteredUsers = subUsers.filter((u) => {
    const matchesSearch =
      (u.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.number || "").includes(searchTerm)
    const matchesStatus =
      statusFilter === "all" ? true : (u.userStatus || "active") === statusFilter
    return matchesSearch && matchesStatus
  })

  // Open Transfer Modal
  const openTransferModal = (targetUserId = "") => {
    setSelectedUserForTransfer(targetUserId || (subUsers[0]?._id || ""))
    setTransferAmount("")
    setTransferDesc("")
    setShowTransferModal(true)
  }

  const handleTransferSubmit = async (e) => {
    e.preventDefault()
    if (!selectedUserForTransfer) {
      Swal.fire({ icon: "error", title: "Error", text: "Please select a user to transfer balance to", confirmButtonColor: "#4A7C59" })
      return
    }
    const numAmount = Number(transferAmount)
    if (!numAmount || numAmount <= 0) {
      Swal.fire({ icon: "error", title: "Error", text: "Please enter a valid transfer amount", confirmButtonColor: "#4A7C59" })
      return
    }

    const recipient = subUsers.find((u) => String(u._id) === String(selectedUserForTransfer))
    const recipientName = recipient?.username || recipient?.email || "User"

    const confirmRes = await Swal.fire({
      title: "Confirm Balance Transfer",
      text: `Transfer ${formatCurrency(numAmount)} to ${recipientName}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, Transfer Now",
    })

    if (!confirmRes.isConfirmed) return

    try {
      setTransferProcessing(true)
      const res = await transferBalance(selectedUserForTransfer, numAmount, transferDesc || "Reseller balance transfer")
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Transfer Successful!",
          text: `${formatCurrency(numAmount)} transferred to ${recipientName}`,
          confirmButtonColor: "#4A7C59",
        })
        setShowTransferModal(false)
        loadDashboardData()
      } else {
        Swal.fire({
          icon: "error",
          title: "Transfer Failed",
          text: res.error || "Could not transfer balance",
          confirmButtonColor: "#EF4444",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Transfer failed",
        confirmButtonColor: "#EF4444",
      })
    } finally {
      setTransferProcessing(false)
    }
  }

  // Derived Calculations
  const attemptedToday = (safeToday.sent || 0) + (safeToday.delivered || 0) + (safeToday.read || 0) + (safeToday.failed || 0)
  const deliveredOrReadToday = (safeToday.delivered || 0) + (safeToday.read || 0)
  const successRateToday = attemptedToday > 0 ? Math.min(100, Math.round((deliveredOrReadToday / attemptedToday) * 100)) : 0

  const calculatePercentageChange = (todayVal, yesterdayVal) => {
    if (yesterdayVal === 0) return todayVal > 0 ? 100 : 0
    return Math.round(((todayVal - yesterdayVal) / yesterdayVal) * 100)
  }

  const todayPercentageChange = calculatePercentageChange(safeToday.total || 0, yesterdayData.total || 0)

  const getBoundedPercentage = (value, base) => {
    if (!base) return 0
    return Math.min(100, Math.round((Number(value || 0) / Number(base || 0)) * 100))
  }

  // Chart Configurations
  const messageChartData = {
    labels: weeklyData.labels,
    datasets: [
      {
        label: "Messages Sent",
        data: weeklyData.messages,
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        pointBackgroundColor: "#2563eb",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Delivered",
        data: weeklyData.delivered,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        pointBackgroundColor: "#10b981",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Read",
        data: weeklyData.read,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.12)",
        pointBackgroundColor: "#f59e0b",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Failed",
        data: weeklyData.failed,
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.12)",
        pointBackgroundColor: "#ef4444",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
    ],
  }

  const statusValues = [safeToday.sent, safeToday.delivered, safeToday.read, safeToday.failed, safeToday.pending]
  const hasStatusData = statusValues.some((v) => v > 0)

  const statusChartData = {
    labels: hasStatusData ? ["Sent", "Delivered", "Read", "Failed", "Pending"] : ["No data"],
    datasets: [
      {
        data: hasStatusData ? statusValues : [1],
        backgroundColor: hasStatusData ? ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#94a3b8"] : ["#e5e7eb"],
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: { legend: { position: "top" }, tooltip: { enabled: hasStatusData } },
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading reseller dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-4 lg:px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                <ChartBarIcon className="w-8 h-8 inline-block mr-3 text-green-600" />
                Welcome {user?.username || "Reseller"}!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
                Here's what's happening with your sub-users' WhatsApp campaigns today
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  Live Dashboard
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleTimeString("en-US", { hour12: true })}
                </span>
              </div>
            </div>

            <div className="mt-4 lg:mt-0 flex gap-3">
              <button
                onClick={() => openTransferModal()}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <BanknotesIcon className="w-4 h-4 mr-2" />
                Transfer Balance
              </button>
              <button
                onClick={loadDashboardData}
                className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Top 4 Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Today's Messages */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {(safeToday.total || 0).toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Today's Messages</div>
                <div
                  className={`flex items-center mt-2 text-sm ${
                    todayPercentageChange >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {todayPercentageChange >= 0 ? (
                    <ArrowUpIcon className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(todayPercentageChange)}% from yesterday
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <PaperAirplaneIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Sub-Users Managed */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {subUsers.length}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Sub-Users</div>
                <div className="flex items-center mt-2 text-green-600 dark:text-green-400 text-sm">
                  <UsersIcon className="w-4 h-4 mr-1" />
                  Active under reseller
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <UsersIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Reseller Wallet Balance */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(resellerBalance)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Wallet Balance</div>
                <div className="flex items-center mt-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  {formatCurrency(resellerBalance)} Credits
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                <WalletIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {successRateToday}%
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Success Rate</div>
                <div className="flex items-center mt-2 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {deliveredOrReadToday} delivered/read today
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <ChartPieIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Cards (Sent, Delivered, Read, Failed) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { key: "sent", label: "Sent", color: "blue", icon: PaperAirplaneIcon },
            { key: "delivered", label: "Delivered", color: "green", icon: CheckCircleIcon },
            { key: "read", label: "Read", color: "yellow", icon: EyeIcon },
            { key: "failed", label: "Failed", color: "red", icon: XMarkIcon },
          ].map((metric) => {
            const value = safeToday[metric.key] || 0
            const percentage = getBoundedPercentage(value, attemptedToday)

            return (
              <div
                key={metric.key}
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 text-center"
              >
                <div className="mb-4">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br from-${metric.color}-500 to-${metric.color}-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg`}
                  >
                    <metric.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {value.toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">{metric.label}</div>
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  {percentage}% of attempted
                </div>
              </div>
            )
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Message Analytics */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2 text-green-600" />
                  Sub-Users Message Analytics
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                    Live
                  </span>
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Last 7 Days</span>
              </div>
            </div>
            <div className="p-6 h-80">
              <Line data={messageChartData} options={chartOptions} />
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ChartPieIcon className="w-5 h-5 mr-2 text-green-600" />
                Status Distribution
              </h3>
            </div>
            <div className="p-6 h-80">
              <Doughnut data={statusChartData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        {/* Sub-Users Table (User List & Today's Usage & Balance) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <UsersIcon className="w-5 h-5 mr-2 text-green-600" />
                Your Users Overview & Today's Usage
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Live balances and message metrics for users under your reseller account.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search user..."
                  className="pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Current Balance</th>
                  <th className="py-3 px-4 text-center">Today Sent</th>
                  <th className="py-3 px-4 text-center">Today Delivered</th>
                  <th className="py-3 px-4 text-center">Today Read</th>
                  <th className="py-3 px-4 text-center">Today Failed</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="py-8 text-center text-gray-500 dark:text-gray-400">
                      No sub-users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u, idx) => {
                    const stats = userStatsMap[u._id] || { sent: 0, delivered: 0, read: 0, failed: 0 }
                    const userBal = typeof u.balance === "number" ? u.balance : Number(u.balance) || 0

                    return (
                      <tr key={u._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="py-3.5 px-4 font-medium text-gray-400">{idx + 1}</td>
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-900 dark:text-white">{u.username || "User"}</div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">{u.email}</div>
                          {u.number && <div className="text-gray-400 text-xs">{u.number}</div>}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 capitalize">
                            {u.role || "user"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(userBal)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-blue-600 dark:text-blue-400">
                          {stats.sent}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-green-600 dark:text-green-400">
                          {stats.delivered}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-yellow-600 dark:text-yellow-400">
                          {stats.read}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-red-600 dark:text-red-400">
                          {stats.failed}
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              (u.userStatus || "active") === "active"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {u.userStatus || "active"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={() => openTransferModal(u._id)}
                            className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-yellow-600 hover:from-green-700 hover:to-yellow-700 text-white rounded-lg text-xs font-semibold transition-all shadow-sm"
                          >
                            <BanknotesIcon className="w-3.5 h-3.5 mr-1" />
                            Transfer
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Balance Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                <BanknotesIcon className="w-5 h-5 mr-2 text-green-600" />
                Transfer Balance to Sub-User
              </h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleTransferSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Sub-User *
                </label>
                <select
                  value={selectedUserForTransfer}
                  onChange={(e) => setSelectedUserForTransfer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                  required
                >
                  <option value="" disabled>-- Select User --</option>
                  {subUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.username || u.email} ({formatCurrency(u.balance || 0)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Transfer Amount (₹) *
                </label>
                <input
                  type="number"
                  min="1"
                  step="any"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="Enter amount (e.g. 500)"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Your wallet balance: <span className="font-semibold text-green-600">{formatCurrency(resellerBalance)}</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Description / Note (Optional)
                </label>
                <input
                  type="text"
                  value={transferDesc}
                  onChange={(e) => setTransferDesc(e.target.value)}
                  placeholder="e.g. Monthly topup"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferProcessing}
                  className="px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-green-600 to-yellow-600 hover:from-green-700 hover:to-yellow-700 rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  {transferProcessing && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                  Confirm Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reseller
